import { StorageUrl } from "@pdfking/storages"
import type { URLString } from "@pdfking/structs"

// Whatever the store is keyed by — a path, for a filesystem. Read off the
// store's own `get` rather than asked for, so the url takes exactly the name
// the thing it points into is read by.
type StoreKey<T> = T extends { get(key: infer K): unknown } ? K : never

/** A store whose entries answer to a url of this platform's making.
 *
 * `fs:` is the name that means "whatever this platform keeps files in" — Cache
 * storage in a browser, a directory under the cache root in node — so it is
 * the one url a store cannot mint for itself: what the authority names is not
 * the storage but the role, and the role is resolved here. Which is what makes
 * it portable in a way `opfs:` and `file:` are not: the same url read on
 * either platform finds the same entry, because each side looks it up in the
 * storage that platform uses.
 *
 * The url overrides whatever the storage underneath answered: a backend that
 * names its entries some other way still holds them for us, under a name we
 * gave it, and that name is the one a reader of ours has to be handed. */
export const ImplementStoreUrl = <T extends { get(key: string): unknown }>(
  protocol: string,
  name: string,
  store: T
): T & { url: (key: StoreKey<T>) => URLString } => ({
  ...store,
  url: (key: StoreKey<T>): URLString => StorageUrl(protocol, name, key as string),
})
